import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

interface ProgressData {
  completionPercentage?: number;
  budgetConsumptionPercentage?: number;
  openSnagsCount?: number;
  actualStartDate?: string;
  progressData?: {
    projectCompletionPercentage?: number;
    budgetComsumption?: number;
    openIssues?: number;
  };
}

interface TableData {
  resourceSummaryData?: any[];
  toolsMachineryData?: any[];
  materialSummaryData?: any[];
  dayProgressData?: any[];
  completionDetailsData?: any[];
  budgetConsumptionData?: any[];
  openIssuesData?: any[];
}

/**
 * Helper function to draw progress bars in PDF
 */
const drawProgressBar = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  percentage: number,
  label: string
) => {
  // Draw label
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(label, x, y);

  // Draw background (gray)
  doc.setFillColor(240, 240, 240);
  doc.rect(x, y + 2, width, height, "F");

  // Draw progress (blue to green gradient effect - using blue)
  const progressWidth = (width * percentage) / 100;
  if (percentage > 0) {
    doc.setFillColor(16, 142, 233); // Blue color
    doc.rect(x, y + 2, progressWidth, height, "F");
  }

  // Draw border
  doc.setDrawColor(200, 200, 200);
  doc.rect(x, y + 2, width, height, "S");

  // Draw percentage text
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`${percentage}%`, x + width + 5, y + 6);
};

/**
 * Generate Date-wise Breakdown PDF
 * @param selectedDate - Progress data for the selected date
 * @param tableData - All table data to include in the PDF
 */
export const generateDatewiseBreakdownPDF = (
  selectedDate: ProgressData,
  tableData: TableData
) => {
  const {
    resourceSummaryData = [],
    toolsMachineryData = [],
    materialSummaryData = [],
    dayProgressData = [],
    completionDetailsData = [],
    budgetConsumptionData = [],
    openIssuesData = [],
  } = tableData;

  const doc = new jsPDF();
  let yPosition = 20;

  // Title
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Date-wise Breakdown Report", 14, yPosition);
  yPosition += 10;

  // Date
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(`Date: ${selectedDate?.actualStartDate || "N/A"}`, 14, yPosition);
  yPosition += 15;

  // Progress Till Date Section
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Progress Till Date", 14, yPosition);
  yPosition += 10;

  // Use progressData from API if available, otherwise fall back to old values
  const completionPercentage =
    selectedDate?.progressData?.projectCompletionPercentage ??
    selectedDate?.completionPercentage ??
    0;
  const budgetPercentage =
    selectedDate?.progressData?.budgetComsumption ??
    selectedDate?.budgetConsumptionPercentage ??
    0;
  const openIssuesCount =
    selectedDate?.progressData?.openIssues ??
    selectedDate?.openSnagsCount ??
    0;

  // Completion Progress Bar
  drawProgressBar(doc, 14, yPosition, 120, 8, Number(completionPercentage.toFixed(2)), "Completion %");
  yPosition += 18;

  // Budget Consumption Progress Bar
  drawProgressBar(doc, 14, yPosition, 120, 8, Number(budgetPercentage.toFixed(2)), "Budget Consumption %");
  yPosition += 18;

  // Open Issues Count
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Open Issues Count", 14, yPosition);
  doc.setFont("helvetica", "normal");
  doc.text(`: ${openIssuesCount}`, 60, yPosition);
  yPosition += 12;

  // Resource Summary Table
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Resource Summary", 14, yPosition);
  yPosition += 5;

  if (resourceSummaryData.length > 0) {
    autoTable(doc, {
      startY: yPosition,
      head: [["Activity", "Task", "Labour\nCategory", "Resource\nPlanned", "Resource\nat Site"]],
      body: resourceSummaryData.map((item) => [
        item.activityName || "N/A",
        item.taskName || "N/A",
        item.laborCategory || "N/A",
        item.plannedResource || "N/A",
        item.actualResource || "N/A",
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [41, 128, 185],
        halign: "center",
        valign: "middle",
        minCellHeight: 12,
      },
      margin: { top: 10 },
    });
    yPosition = (doc as any).lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("No data available", 14, yPosition);
    yPosition += 15;
  }

  // Tools and Machinery Table
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Tools and Machinery", 14, yPosition);
  yPosition += 5;

  if (toolsMachineryData.length > 0) {
    autoTable(doc, {
      startY: yPosition,
      head: [["Activity", "Task", "Type", "Name", "Planned", "At Site"]],
      body: toolsMachineryData.map((item) => [
        item.activityName || "N/A",
        item.taskName || "N/A",
        item.toolType || "N/A",
        item.toolName || "N/A",
        item.planned || "N/A",
        item.atSite || "N/A",
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [41, 128, 185],
        halign: "center",
        valign: "middle",
        minCellHeight: 12,
      },
    });
    yPosition = (doc as any).lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("No data available", 14, yPosition);
    yPosition += 15;
  }

  // Material Summary Table
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Material Summary", 14, yPosition);
  yPosition += 5;

  if (materialSummaryData.length > 0) {
    autoTable(doc, {
      startY: yPosition,
      head: [["Material", "Issue\nQTY", "Utilized\nQTY", "Returned", "Damaged"]],
      body: materialSummaryData.map((item) => [
        item.materialName || "N/A",
        `${item.issuedQty || "N/A"} ${item.uom || ""}`,
        `${item.utilizedQty || "N/A"} ${item.uom || ""}`,
        `${item.returnQty || "N/A"} ${item.uom || ""}`,
        `${item.damagedQty || "N/A"} ${item.uom || ""}`,
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [41, 128, 185],
        halign: "center",
        valign: "middle",
        minCellHeight: 12,
      },
    });
    yPosition = (doc as any).lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("No data available", 14, yPosition);
    yPosition += 15;
  }

  // Day Progress Table
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Day Progress", 14, yPosition);
  yPosition += 5;

  if (dayProgressData.length > 0) {
    autoTable(doc, {
      startY: yPosition,
      head: [["Activity", "Task", "QTY", "UOM", "Executed\nToday", "Executed\nTill Date"]],
      body: dayProgressData.map((item) => [
        item.activityName || "N/A",
        item.taskName || "N/A",
        item.workQuantity || "N/A",
        item.uom || "N/A",
        item.executedToday || "N/A",
        item.executedTilldate || "N/A",
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [41, 128, 185],
        halign: "center",
        valign: "middle",
        minCellHeight: 12,
      },
    });
    yPosition = (doc as any).lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("No data available", 14, yPosition);
    yPosition += 15;
  }

  // Completion Percentage Details - only add new page if not enough space
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Completion Percentage Details", 14, yPosition);
  yPosition += 8;

  if (completionDetailsData.length > 0) {
    autoTable(doc, {
      startY: yPosition,
      head: [["Activity", "Task", "QTY", "UOM", "Executed\nTill Date", "Progress\n%"]],
      body: completionDetailsData.map((item) => [
        item.activityName || "N/A",
        item.taskName || "N/A",
        item.workQuantity || "N/A",
        item.workUom || "N/A",
        item.executedQty || "N/A",
        `${item.taskCompletionPercentage ? Number(item.taskCompletionPercentage).toFixed(2) : 0}%`,
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [41, 128, 185],
        halign: "center",
        valign: "middle",
        minCellHeight: 12,
      },
    });
    yPosition = (doc as any).lastAutoTable.finalY + 15;
  } else {
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("No data available", 14, yPosition);
    yPosition += 15;
  }

  // Budget Consumption Details - only add new page if not enough space
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Budget Consumption Details", 14, yPosition);
  yPosition += 8;

  if (budgetConsumptionData.length > 0) {
    autoTable(doc, {
      startY: yPosition,
      head: [["Category", "Budget", "Spend", "%"]],
      body: budgetConsumptionData.map((item) => [
        item.budgetCategoryName || "N/A",
        item.budget ? `Rs. ${Number(item.budget).toLocaleString()}` : "N/A",
        item.budgetSpend ? `Rs. ${Number(item.budgetSpend).toLocaleString()}` : "N/A",
        `${item.budgetComsumption ? Number(item.budgetComsumption).toFixed(2) : 0}%`,
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [41, 128, 185],
        halign: "center",
        valign: "middle",
        minCellHeight: 12,
      },
    });
    yPosition = (doc as any).lastAutoTable.finalY + 15;
  } else {
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("No data available", 14, yPosition);
    yPosition += 15;
  }

  // Open Issues List - only add new page if not enough space
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Open Issues List", 14, yPosition);
  yPosition += 8;

  if (openIssuesData.length > 0) {
    autoTable(doc, {
      startY: yPosition,
      head: [["Task\nName", "Issue\nType", "Issue\nName", "Priority", "Status", "Raised\nOn", "Assigned\nTo"]],
      body: openIssuesData.map((item) => [
        item.taskName || "N/A",
        item.issueType || "N/A",
        item.issueName || "N/A",
        item.issuePriority || "N/A",
        item.issueStatus || "N/A",
        item.issueRaisedOn ? dayjs(item.issueRaisedOn).format("DD-MMM-YYYY") : "N/A",
        item.assignedto || "N/A",
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [41, 128, 185],
        halign: "center",
        valign: "middle",
        minCellHeight: 12,
      },
      styles: { fontSize: 9 },
    });
  } else {
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("No data available", 14, yPosition);
  }

  // Save PDF
  doc.save(`Date_wise_Breakdown_${selectedDate?.actualStartDate || "Report"}.pdf`);
};
